"use client";
import { Button } from "../components/ui/Button";
import { Input } from "../components/ui/Input";
import { Label } from "../components/ui/Label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/Select";
import { Checkbox } from "../components/ui/Checkbox";
import { ArrowLeft, Plus, Trash2, X, Upload, Percent, DollarSign, Eye, Settings, ChevronLeft, ChevronRight, Download } from "lucide-react";
import { useState, useEffect, useRef, useCallback } from "react";
import { useParams } from "react-router-dom";
import { brandService } from "../services/brands";
import { categoryService } from "../services/categories";
import { shippingService } from "../services/shippings";
import { productService } from "../services/products";
import { userService } from "../services/users";
import { influencerService } from "../services/influencers";
import { toast } from "sonner";
import { decodeHtmlEntities, getImageUrl, slugify } from "../utils";
import { AddCategoryModal } from "../components/dialogs/AddCategoryModal";
import { AddBrandModal } from "../components/dialogs/AddBrandModal";
import { AddInfluencerModal } from "../components/dialogs/AddInfluencerModal";
import { AddSupplierModal } from "../components/dialogs/AddSupplierModal";
import { AddShippingModal } from "../components/dialogs/AddShippingModal";
import { cn } from "../components/ui/utils";
import { AsyncSelect } from "../components/AsyncSelect";

function MultiSelect({ options, selected, onChange, placeholder, hasError }: { options: any[], selected: string[], onChange: (vals: string[]) => void, placeholder: string, hasError?: boolean }) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const ref = useRef<HTMLDivElement>(null);

  const filteredOptions = options.filter(opt =>
    opt.label.toLowerCase().includes(search.toLowerCase())
  );

  useEffect(() => {
    const handleClick = (e: MouseEvent) => { if (ref.current && !ref.current.contains(e.target as Node)) { setOpen(false); setSearch(""); } };
    if (open) document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, [open]);

  return (
    <div ref={ref} className="relative flex-1">
      <div className={cn("min-h-10 border rounded-md p-1.5 bg-white cursor-pointer flex flex-wrap gap-1.5 transition-colors", hasError ? "border-red-500" : "border-gray-300")} onClick={() => setOpen(!open)}>
        {selected.length === 0 && <span className="text-gray-400 text-sm ml-1.5 mt-1">{placeholder}</span>}
        {selected.map(val => {
          const opt = options.find(o => String(o.value) === String(val));
          return (
            <div key={val} className="bg-gray-100 text-[#68686A] px-2 py-0.5 rounded-full text-xs flex items-center gap-1 border border-gray-200">
              {opt?.label || val}
              <X className="h-3 w-3 hover:text-red-500" onClick={(e) => { e.stopPropagation(); onChange(selected.filter(v => v !== val)); }} />
            </div>
          );
        })}
      </div>
      {open && (
        <div className="absolute z-50 mt-1 w-full bg-white border border-gray-200 rounded-md shadow-lg max-h-60 overflow-y-auto p-2 space-y-1">
          <input
            type="text"
            className="w-full border border-gray-200 rounded-md p-2 text-sm sticky top-0 bg-white mb-2 focus:ring-2 focus:ring-[#D4C070] outline-none"
            placeholder="Buscar categoria..."
            autoFocus
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            onClick={(e) => e.stopPropagation()}
          />
          {filteredOptions.length > 0 ? filteredOptions.map(opt => (
            <div key={opt.value} className={cn("px-3 py-2 text-sm cursor-pointer rounded hover:bg-gray-50", selected.includes(String(opt.value)) && "bg-[#D4C070]/10 text-[#D4C070] font-bold")}
              onClick={() => {
                const s = String(opt.value);
                if (selected.includes(s)) onChange(selected.filter(v => v !== s));
                else onChange([...selected, s]);
              }}>
              {opt.label}
            </div>
          )) : (
            <div className="px-3 py-2 text-sm text-gray-400 italic">Nenhum resultado</div>
          )}
        </div>
      )}
    </div>
  );
}



interface NovoProductFormProps { onBack: () => void; productData?: any; }

export default function NovoProductForm({ onBack, productData: initialProductData }: NovoProductFormProps) {
  const { id: urlId } = useParams();
  const [productData, setProductData] = useState<any>(initialProductData);
  const [loading, setLoading] = useState(false);
  const [_brands, setBrands] = useState<any[]>([]);
  const [uploadProgress, setUploadProgress] = useState<{ current: number; total: number; filename?: string } | null>(null);

  const [categories, setCategories] = useState<any[]>([]);
  const [shippings, setShippings] = useState<any[]>([]);
  const [errors, setErrors] = useState<Record<string, boolean>>({});
  const [formData, setFormData] = useState({
    name: "", sku: "", slug: "", brand_id: null as string | null,
    price: "", originalPrice: "", juros: "", juros_type: "value" as "value" | "percent",
    avista_price: "", totalParcelado: "",
    quantity: "1", description: "", status: "published", is_on_sale: false,
    sale_price: "", featured: false, new_in: false, live: false, on_request: false, product_comission: "", referral_commission_rate: "",
    certificate_price: "", saled_at: "", paidout_at: "", returned_at: "", available_at: "", bump_available_at: true, link: "", adicionais: "",
    cor: "", cor_ferragem: "", estado: "", material: "", numero: "", origem: "",
    salto: "", sola: "", tamanho: "", altura: "", largura: "", profundidade: "",
    user_id: null as string | null, influencer_id: null as string | null, shipping_id: null as string | null,
    categories: [] as string[],
    // No estado local, usamos path e is_primary para facilitar a UI
    images: [] as { image: string, is_primary: boolean }[],
    seo_title: "", seo_description: ""
  });

  const [selectedNames, setSelectedNames] = useState<Record<string, string>>({});
  const [modals, setModals] = useState({ brand: false, category: false, subcategory: false, influencer: false, supplier: false, shipping: false });

  const [lastSupplierSku, setLastSupplierSku] = useState<string | null>(null);

  useEffect(() => {
    if (formData.user_id) {
      productService.getAllProducts({ user_id: formData.user_id, sort_by: 'created_at', sort_order: 'desc', per_page: 1 })
        .then(res => {
          const products = res.data?.data || res.data || [];
          if (products.length > 0) {
            setLastSupplierSku(products[0].sku);
          } else {
            setLastSupplierSku("Nenhum");
          }
        })
        .catch(() => setLastSupplierSku(null));
    } else {
      setLastSupplierSku(null);
    }
  }, [formData.user_id]);

  const fetchInfluencers = useCallback(async (params: any) => {
    const data = await influencerService.getInfluencers(1, { ...params, per_page: 100, active: true });
    return data;
  }, []);

  const fetchSuppliers = useCallback(async (params: any) => {
    if (!params.search || params.search.trim().length < 2) return [];
    const res = await userService.getAll({ ...params, has_code: 1, sort: 'first_name', order: 'asc' });
    return res.data;
  }, []);

  const fetchBrands = useCallback(async (params: any) => {
    const res = await brandService.getAll({ ...params, per_page: 50 });
    return res.data;
  }, []);

  const fetchShippings = useCallback(async (params?: { search?: string }) => {
    const res = await shippingService.getAll();
    const items = res.data?.data || res.data || [];
    if (params?.search) {
      const q = params.search.toLowerCase();
      return items.filter((s: any) =>
        (s.name && s.name.toLowerCase().includes(q)) ||
        (s.code && s.code.toLowerCase().includes(q))
      );
    }
    return items;
  }, []);

  const handleImageUpload = async (e: React.ChangeEvent<HTMLInputElement> | React.DragEvent) => {
    let files: FileList | File[] | null = null;
    let targetInput: HTMLInputElement | null = null;

    if ('files' in e.target && (e.target as any).files) {
      files = (e.target as any).files;
      targetInput = e.target as HTMLInputElement;
    } else if ('dataTransfer' in e && e.dataTransfer.files) {
      e.preventDefault();
      files = e.dataTransfer.files;
    }

    if (!files || files.length === 0) return;

    const filesArray = Array.from(files);

    // Reset do valor do input para permitir selecionar o mesmo arquivo novamente se necessário
    if (targetInput) {
      targetInput.value = '';
    }

    setLoading(true);
    const total = filesArray.length;
    setUploadProgress({ current: 0, total });

    const MAX_CONCURRENCY = 2; // Processa no máximo 2 uploads simultâneos para evitar sobrecarga no servidor
    const MAX_FILE_SIZE = 25 * 1024 * 1024; // 25MB

    const successfulPaths: string[] = [];
    const uploadErrors: { filename: string; reason: string }[] = [];
    let completedCount = 0;

    const extractErrorMessage = (err: any): string => {
      if (err.response?.data?.message) return err.response.data.message;
      if (err.response?.data?.error) return err.response.data.error;
      if (err.response?.data?.errors) {
        const msgs = Object.values(err.response.data.errors).flat();
        if (msgs.length > 0) return String(msgs[0]);
      }
      if (err.response?.status === 413) return "Arquivo excede o tamanho máximo permitido pelo servidor";
      if (err.response?.status === 504 || err.code === 'ECONNABORTED') return "Tempo limite do servidor excedido (504)";
      if (err.message) return err.message;
      return "Erro desconhecido durante o upload";
    };

    const queue = [...filesArray];
    const worker = async () => {
      while (queue.length > 0) {
        const file = queue.shift();
        if (!file) break;

        // Validação prévia de imagem e tamanho
        const isImage = file.type.startsWith('image/') || /\.(jpg|jpeg|png|webp|gif|avif|heic)$/i.test(file.name);
        if (!isImage) {
          uploadErrors.push({ filename: file.name, reason: "Formato inválido (não é imagem)" });
          completedCount++;
          setUploadProgress({ current: completedCount, total, filename: file.name });
          continue;
        }

        if (file.size > MAX_FILE_SIZE) {
          uploadErrors.push({ filename: file.name, reason: `Tamanho excede ${(MAX_FILE_SIZE / (1024 * 1024)).toFixed(0)}MB` });
          completedCount++;
          setUploadProgress({ current: completedCount, total, filename: file.name });
          continue;
        }

        try {
          const res = await productService.uploadImage(file);
          const path = res.data?.path || res.data?.url || res.data;
          if (path && typeof path === 'string') {
            successfulPaths.push(path);
          } else {
            uploadErrors.push({ filename: file.name, reason: "Resposta do servidor sem caminho da imagem" });
          }
        } catch (err: any) {
          console.error(`[Upload error] ${file.name}:`, err);
          uploadErrors.push({ filename: file.name, reason: extractErrorMessage(err) });
        } finally {
          completedCount++;
          setUploadProgress({ current: completedCount, total, filename: file.name });
        }
      }
    };

    try {
      const workers = Array(Math.min(MAX_CONCURRENCY, filesArray.length)).fill(null).map(() => worker());
      await Promise.all(workers);

      if (successfulPaths.length > 0) {
        setFormData(prev => {
          const currentCount = prev.images.length;
          const newImgs = successfulPaths.map((path, index) => ({
            image: path,
            is_primary: currentCount === 0 && index === 0
          }));
          return { ...prev, images: [...prev.images, ...newImgs] };
        });
        setErrors(prev => ({ ...prev, images: false }));
      }

      if (uploadErrors.length === 0) {
        toast.success(`${successfulPaths.length} foto(s) enviada(s) com sucesso!`);
      } else if (successfulPaths.length > 0) {
        const errorSummary = uploadErrors.map(e => `${e.filename}: ${e.reason}`).join(" | ");
        toast.warning(
          `${successfulPaths.length} de ${total} foto(s) enviadas. ${uploadErrors.length} falharam: ${errorSummary}`,
          { duration: 9000 }
        );
      } else {
        const errorSummary = uploadErrors.map(e => `${e.filename}: ${e.reason}`).join(" | ");
        toast.error(`Falha no upload das foto(s): ${errorSummary}`, { duration: 9000 });
      }
    } catch (err: any) {
      console.error("Erro inesperado no lote de upload:", err);
      toast.error("Ocorreu um erro inesperado ao processar o lote de imagens.");
    } finally {
      setLoading(false);
      setUploadProgress(null);
    }
  };

  const moveImage = (index: number, direction: 'left' | 'right') => {
    const newImages = [...formData.images];
    const targetIndex = direction === 'left' ? index - 1 : index + 1;
    if (targetIndex < 0 || targetIndex >= newImages.length) return;

    const temp = newImages[index];
    newImages[index] = newImages[targetIndex];
    newImages[targetIndex] = temp;

    setFormData(prev => ({ ...prev, images: newImages }));
  };

  const getFormattedPhotoName = (productName: string, sku: string, index: number, originalUrl: string) => {
    const base = slugify(productName || sku || "produto").replace(/-/g, "_");
    const cleanUrl = originalUrl ? originalUrl.split('?')[0] : '';
    const extMatch = cleanUrl.match(/\.(jpg|jpeg|png|webp|gif|svg|avif)$/i);
    const ext = extMatch ? extMatch[1].toLowerCase() : "jpg";
    return `${base}_foto_${index + 1}.${ext}`;
  };

  const handleDownloadSingleImage = (imgUrl: string, index: number) => {
    const fullUrl = getImageUrl(imgUrl);
    const fileName = getFormattedPhotoName(formData.name, formData.sku, index, imgUrl);
    const siteUrl = (import.meta.env.VITE_STORE_URL || "").replace(/\/$/, "");
    const downloadUrl = `${siteUrl}/api/upload/download-image?url=${encodeURIComponent(fullUrl)}&filename=${encodeURIComponent(fileName)}`;

    const link = document.createElement("a");
    link.href = downloadUrl;
    link.target = "_blank";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  useEffect(() => {
    if (!productData && urlId) {
      productService.getProductById(Number(urlId)).then(res => setProductData(res.data?.data || res.data));
    }
  }, [urlId, productData]);

  useEffect(() => {
    categoryService.getAll().then(res => {
      const items = res.data?.data || res.data || [];
      const flattened = items.reduce((acc: any[], item: any) => {
        if (item.slug === 'sale' || item.name?.toUpperCase() === 'SALE') return acc;
        if (item.parent_id > 0 && item.parent) {
          item.name = `${decodeHtmlEntities(item.parent.name)} > ${decodeHtmlEntities(item.name)}`;
        } else {
          item.name = decodeHtmlEntities(item.name);
        }
        acc.push(item);
        return acc;
      }, []);
      setCategories(flattened);
    });
    shippingService.getAll().then(res => setShippings(res.data?.data || res.data || []));
  }, []);

  // Sincronização automática das flags baseada nas categorias selecionadas
  useEffect(() => {
    const liveCat = categories.find((c: any) => c.slug === "live" || c.name?.toUpperCase() === "LIVE");
    const liveId = liveCat ? String(liveCat.id) : null;

    const hasFeatured = formData.categories.includes("4609");
    const hasNewIn = formData.categories.includes("4535");
    const hasLive = liveId ? formData.categories.includes(liveId) : false;

    if (hasFeatured !== formData.featured || hasNewIn !== formData.new_in || hasLive !== formData.live) {
      setFormData(prev => ({
        ...prev,
        featured: hasFeatured,
        new_in: hasNewIn,
        live: hasLive
      }));
    }
  }, [formData.categories, categories]);

  useEffect(() => {
    if (productData) {
      const data = productData.data || productData;
      const liveCat = categories.find((c: any) => c.slug === "live" || c.name?.toUpperCase() === "LIVE");
      const liveId = liveCat ? String(liveCat.id) : null;

      setFormData(prev => ({
        ...prev,
        ...data,
        bump_available_at: true,
        brand_id: data.brand?.id ? String(data.brand.id) : (data.brand_id ? String(data.brand_id) : null),
        user_id: data.user?.id ? String(data.user.id) : (data.user_id ? String(data.user_id) : null),
        influencer_id: data.influencer?.id ? String(data.influencer.id) : (data.influencer_id ? String(data.influencer_id) : null),
        shipping_id: data.shipping_id ? String(data.shipping_id) : null,
        featured: data.categories?.some((c: any) => String(c.id) === "4609"),
        new_in: data.categories?.some((c: any) => String(c.id) === "4535"),
        live: data.categories?.some((c: any) => String(c.id) === liveId || c.slug === "live"),
        on_request: !!data.on_request,
        categories: data.categories?.map((c: any) => String(c.id)) || [],
        cor: data.attributes?.cor || data.cor || "",
        material: data.attributes?.material || data.material || "",
        tamanho: data.attributes?.tamanho || data.tamanho || "",
        estado: data.attributes?.estado || data.estado || "",
        numero: data.attributes?.numero || data.numero || "",
        salto: data.attributes?.salto || data.salto || "",
        sola: data.attributes?.sola || data.sola || "",
        origem: data.attributes?.origem || data.origem || "",
        cor_ferragem: data.attributes?.cor_ferragem || data.cor_ferragem || "",
        altura: data.attributes?.altura || "",
        largura: data.attributes?.largura || "",
        profundidade: data.attributes?.profundidade || "",
        adicionais: data.attributes?.adicionais || data.adicionais || "",
        // Mapeia do formato do backend (image, order) para o local (image, is_primary)
        images: data.images?.map((img: any, idx: number) => ({
          image: img.image || img.path || img,
          is_primary: idx === 0 || img.order === 0
        })) || [],
        referral_commission_rate: data.referral_commission_rate !== undefined && data.referral_commission_rate !== null ? String(data.referral_commission_rate) : "",
        seo_title: data.seo?.title || "",
        seo_description: data.seo?.description || ""
      }));
      const names: any = {};
      if (data.brand) names[data.brand.id] = data.brand.name;
      if (data.user) {
        const emailPart = data.user.email ? ` (${data.user.email})` : "";
        const fullName = [data.user.first_name, data.user.last_name].filter(Boolean).join(" ") || data.user.name || "";
        names[data.user.id] = `[${data.user.code || data.user.id}] ${fullName}${emailPart}`;
      }
      if (data.influencer) names[data.influencer.id] = data.influencer.name;
      if (data.shipping_id && shippings.length > 0) {
        const s = shippings.find(x => String(x.id) === String(data.shipping_id));
        if (s) names[data.shipping_id] = s.name;
      }
      setSelectedNames(prev => ({ ...prev, ...names }));
    }
  }, [productData, shippings]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    const newErrors: Record<string, boolean> = {};
    if (!formData.user_id || formData.user_id === "nenhum") newErrors.user_id = true;
    if (!formData.name) newErrors.name = true;
    if (!formData.sku) newErrors.sku = true;
    if (!formData.brand_id || formData.brand_id === "nenhum") newErrors.brand_id = true;
    if (!formData.shipping_id || formData.shipping_id === "nenhum") newErrors.shipping_id = true;
    if (!formData.categories || formData.categories.length === 0) newErrors.categories = true;
    if (!formData.price) newErrors.price = true;
    if (formData.quantity === undefined || formData.quantity === null || formData.quantity === "") newErrors.quantity = true;
    if (!formData.images || formData.images.length === 0) newErrors.images = true;

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);

      const fieldNamesMap: Record<string, string> = {
        user_id: "Fornecedor",
        name: "Nome do Produto",
        sku: "SKU / Referência",
        brand_id: "Marca",
        shipping_id: "Regra de Frete",
        categories: "Categorias",
        price: "Preço à Vista",
        quantity: "Quantidade em Estoque",
        images: "Galeria de Fotos"
      };

      const missingFields = Object.keys(newErrors)
        .map(key => fieldNamesMap[key] || key)
        .join(", ");

      toast.error(`Por favor, preencha os campos obrigatórios pendentes: ${missingFields}.`);

      // Rola até o primeiro elemento com erro
      const firstErrorKey = Object.keys(newErrors)[0];
      const element = document.getElementById(`field-${firstErrorKey}`);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
      return;
    }

    setErrors({});
    setLoading(true);
    try {
      const {
        cor, cor_ferragem, estado, material, numero, origem, salto, sola, tamanho,
        altura, largura, profundidade, adicionais,
        seo_title, seo_description,
        featured, new_in, live, bump_available_at, categories: formCategoryIds, // Extraímos para processar
        ...base
      } = formData;

      // Injeta categorias especiais se as flags estiverem marcadas
      const finalCategories = [...formCategoryIds];
      if (featured && !finalCategories.includes("4609")) finalCategories.push("4609");
      if (new_in && !finalCategories.includes("4535")) finalCategories.push("4535");

      const liveCat = categories.find((c: any) => c.slug === "live" || c.name?.toUpperCase() === "LIVE");
      const liveId = liveCat ? String(liveCat.id) : null;
      if (live && liveId && !finalCategories.includes(liveId)) finalCategories.push(liveId);

      // Transforma para o formato esperado pelo backend: { image: string, order: number }
      const finalImgs = formData.images.map((img, idx) => ({
        image: img.image,
        order: idx
      }));

      const payload: any = {
        ...base,
        bump_available_at: !!bump_available_at,
        brand_id: base.brand_id && base.brand_id !== "nenhum" ? Number(base.brand_id) : null,
        user_id: base.user_id && base.user_id !== "nenhum" ? Number(base.user_id) : null,
        influencer_id: base.influencer_id && base.influencer_id !== "nenhum" ? Number(base.influencer_id) : null,
        shipping_id: base.shipping_id && base.shipping_id !== "nenhum" ? Number(base.shipping_id) : null,
        referral_commission_rate: base.referral_commission_rate === "" || base.referral_commission_rate === null ? null : Number(base.referral_commission_rate),
        categories: finalCategories,
        images: finalImgs,
        attributes: { cor, cor_ferragem, estado, material, numero, origem, salto, sola, tamanho, altura, largura, profundidade, adicionais },
        seo: {
          title: seo_title || formData.name,
          description: seo_description || formData.description
        }
      };

      if (productData?.id) await productService.updateProduct(productData.id, payload);
      else await productService.createProduct(payload);

      toast.success("Salvo com sucesso!");
      onBack();
    } catch (err: any) {
      console.error("Erro ao salvar produto:", err);
      let errorMessage = "Ocorreu um erro ao salvar o produto.";

      if (err.response?.data) {
        if (err.response.data.errors) {
          const messages = Object.values(err.response.data.errors).flat();
          errorMessage = messages.join(" ");
        } else if (err.response.data.message) {
          errorMessage = err.response.data.message;
        } else if (err.response.data.error) {
          errorMessage = err.response.data.error;
        }
      } else if (err.message) {
        errorMessage = err.message;
      }

      toast.error("Erro ao salvar: " + errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const inputClass = "bg-white border-gray-300 focus:border-[#D4C070] focus:ring-[#D4C070]";

  return (
    <main className="flex-1 p-6 bg-gray-50 pb-32">
      <div className="max-w-5xl mx-auto">
        <div className="mb-8 flex items-center justify-between">
          <div>
            <Button variant="ghost" onClick={onBack} className="mb-2 -ml-2 text-gray-500 hover:text-[#D4C070]">
              <ArrowLeft className="h-4 w-4 mr-2" /> Voltar
            </Button>
            <h1 className="text-3xl font-bold text-gray-800">{productData ? "Editar Produto" : "Novo Produto"}</h1>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="space-y-8">
          {/* Identificação e Origem */}
          <div className="bg-white p-8 rounded-2xl border border-gray-100 shadow-sm space-y-6">
            <h2 className="text-xl font-bold text-gray-700 flex items-center gap-2"><Plus className="h-5 w-5 text-[#D4C070]" /> Identificação e Origem</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div id="field-user_id" className="space-y-1 md:col-span-2">
                <Label>Fornecedor <span className="text-red-500">*</span></Label>
                <div className="flex gap-2">
                  <AsyncSelect
                    value={formData.user_id}
                    onChange={(id, lbl, item) => {
                      const nextSku = item?.code ? String(item.code) : formData.sku;
                      setFormData({
                        ...formData,
                        user_id: id,
                        sku: nextSku,
                        slug: slugify(formData.name + " " + nextSku)
                      });
                      if (id) setSelectedNames({ ...selectedNames, [id]: lbl });
                      if (errors.user_id) setErrors(prev => ({ ...prev, user_id: false }));
                    }}
                    fetcher={fetchSuppliers}
                    placeholder="Buscar por código ou nome..."
                    initialDisplay={formData.user_id ? selectedNames[formData.user_id] : ""}
                    getOptionLabel={(u) => {
                      const fullName = [u.first_name, u.last_name].filter(Boolean).join(" ") || u.name || "";
                      return `[${u.code || u.id}] ${fullName}${u.email ? ` (${u.email})` : ""}`;
                    }}
                    renderOption={(u) => (
                      <div className="flex items-center gap-3 py-1.5 w-full">
                        <div className="flex items-center justify-center min-w-[55px] bg-gray-800 text-white text-[10px] font-bold px-2 py-1 rounded border border-gray-700 uppercase tracking-tighter shadow-sm">
                          {u.code || u.id}
                        </div>
                        <div className="flex flex-col min-w-0 flex-1">
                          <span className="font-bold text-gray-900 truncate">
                            {[u.first_name, u.last_name].filter(Boolean).join(" ") || u.name}
                          </span>
                          {u.email && <span className="text-[10px] text-gray-500 truncate font-medium">{u.email}</span>}
                        </div>
                      </div>
                    )}
                    hasError={errors.user_id}
                  />
                  <Button type="button" size="icon" onClick={() => setModals({ ...modals, supplier: true })} className="bg-[#D4C070] text-white shrink-0"><Plus className="h-4 w-4" /></Button>
                </div>
              </div>
              <div id="field-name" className="space-y-1">
                <Label>Nome do Produto <span className="text-red-500">*</span></Label>
                <Input className={cn(inputClass, "text-lg", errors.name && "border-red-500 focus:border-red-500 focus:ring-red-500")} value={formData.name} onChange={(e) => { setFormData({ ...formData, name: e.target.value, slug: slugify(e.target.value + " " + formData.sku) }); if (errors.name) setErrors(prev => ({ ...prev, name: false })); }} />
              </div>
              <div id="field-sku" className="space-y-1">
                <Label>SKU / Referência <span className="text-red-500">*</span></Label>
                <Input className={cn(inputClass, errors.sku && "border-red-500 focus:border-red-500 focus:ring-red-500")} value={formData.sku} onChange={(e) => { setFormData({ ...formData, sku: e.target.value, slug: slugify(formData.name + " " + e.target.value) }); if (errors.sku) setErrors(prev => ({ ...prev, sku: false })); }} />
                {lastSupplierSku && <p className="text-[10px] text-gray-500 font-medium italic mt-1">Último SKU do Fornecedor: <span className="text-gray-700 font-bold">{lastSupplierSku}</span></p>}
              </div>
              <div className="space-y-1">
                <Label>Influenciador</Label>
                <div className="flex gap-2">
                  <AsyncSelect value={formData.influencer_id} onChange={(id, lbl) => { setFormData({ ...formData, influencer_id: id }); if (id) setSelectedNames({ ...selectedNames, [id]: lbl }); }} fetcher={fetchInfluencers} placeholder="Selecionar..." initialDisplay={formData.influencer_id ? selectedNames[formData.influencer_id] : ""} />
                  <Button type="button" size="icon" onClick={() => setModals({ ...modals, influencer: true })} className="bg-[#D4C070] text-white shrink-0"><Plus className="h-4 w-4" /></Button>
                </div>
              </div>
              <div className="space-y-1 md:col-span-2">
                <Label>Link do Produto (Loja)</Label>
                <div className="flex gap-2">
                  <Input className={cn(inputClass, "bg-gray-50 text-gray-400 font-mono text-xs")} value={`${import.meta.env.VITE_STORE_URL}/produtos/${formData.slug || ''}`} readOnly />
                  {productData && (
                    <Button type="button" variant="outline" onClick={() => window.open(`${import.meta.env.VITE_STORE_URL}/produtos/${formData.slug}`, '_blank')} className="shrink-0"><Eye className="h-4 w-4 mr-2" /> Abrir</Button>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Classificação */}
          <div className="bg-white p-8 rounded-2xl border border-gray-100 shadow-sm space-y-6">
            <h2 className="text-xl font-bold text-gray-700 flex items-center gap-2"><Upload className="h-5 w-5 text-[#D4C070]" /> Classificação</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              <div className="space-y-4">
                <div id="field-brand_id" className="space-y-1">
                  <Label>Marca <span className="text-red-500">*</span></Label>
                  <div className="flex gap-2">
                    <AsyncSelect value={formData.brand_id} onChange={(id, lbl) => { setFormData({ ...formData, brand_id: id }); if (id) setSelectedNames({ ...selectedNames, [id]: lbl }); if (errors.brand_id) setErrors(prev => ({ ...prev, brand_id: false })); }} fetcher={fetchBrands} placeholder="Selecione..." initialDisplay={formData.brand_id ? selectedNames[formData.brand_id] : ""} hasError={errors.brand_id} />
                    <Button type="button" size="icon" onClick={() => setModals({ ...modals, brand: true })} className="bg-[#D4C070] text-white shrink-0"><Plus className="h-4 w-4" /></Button>
                  </div>
                </div>
                <div id="field-shipping_id" className="space-y-1">
                  <Label>Regra de Frete <span className="text-red-500">*</span></Label>
                  <div className="flex gap-2">
                    <AsyncSelect
                      value={formData.shipping_id}
                      onChange={(id, lbl) => {
                        setFormData(prev => ({ ...prev, shipping_id: id }));
                        if (id) setSelectedNames(prev => ({ ...prev, [id]: lbl }));
                        if (errors.shipping_id) setErrors(prev => ({ ...prev, shipping_id: false }));
                      }}
                      fetcher={fetchShippings}
                      placeholder="Selecionar frete..."
                      initialDisplay={formData.shipping_id ? selectedNames[formData.shipping_id] : ""}
                      hasError={errors.shipping_id}
                    />
                    <Button type="button" size="icon" onClick={() => setModals({ ...modals, shipping: true })} className="bg-[#D4C070] text-white shrink-0"><Plus className="h-4 w-4" /></Button>
                  </div>
                </div>
              </div>
              <div className="space-y-4">
                <div id="field-categories" className="space-y-1">
                  <Label>Categorias <span className="text-red-500">*</span></Label>
                  <MultiSelect options={categories.map(c => ({ value: c.id, label: c.name }))} selected={formData.categories} onChange={(v) => { setFormData({ ...formData, categories: v }); if (errors.categories) setErrors(prev => ({ ...prev, categories: false })); }} placeholder="Selecione..." hasError={errors.categories} />
                </div>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                  <div className="flex items-center space-x-2 bg-gray-50 p-2.5 rounded-lg border border-gray-100">
                    <Checkbox id="feat" checked={formData.featured} onCheckedChange={(v) => {
                      const isChecked = !!v;
                      const newCats = isChecked
                        ? [...formData.categories, "4609"]
                        : formData.categories.filter(c => c !== "4609");
                      setFormData({ ...formData, featured: isChecked, categories: Array.from(new Set(newCats)) });
                    }} />
                    <Label htmlFor="feat" className="text-[10px] font-bold cursor-pointer uppercase text-gray-500">Destaque</Label>
                  </div>
                  <div className="flex items-center space-x-2 bg-gray-50 p-2.5 rounded-lg border border-gray-100">
                    <Checkbox id="newin" checked={formData.new_in} onCheckedChange={(v) => {
                      const isChecked = !!v;
                      const newCats = isChecked
                        ? [...formData.categories, "4535"]
                        : formData.categories.filter(c => c !== "4535");
                      setFormData({ ...formData, new_in: isChecked, categories: Array.from(new Set(newCats)) });
                    }} />
                    <Label htmlFor="newin" className="text-[10px] font-bold cursor-pointer uppercase text-gray-500">New In</Label>
                  </div>
                  <div className="flex items-center space-x-2 bg-gray-50 p-2.5 rounded-lg border border-gray-100">
                    <Checkbox id="live" checked={formData.live} onCheckedChange={(v) => {
                      const isChecked = !!v;
                      const liveCat = categories.find((c: any) => c.slug === "live" || c.name?.toUpperCase() === "LIVE");
                      const liveId = liveCat ? String(liveCat.id) : null;
                      if (!liveId) return;
                      const newCats = isChecked
                        ? [...formData.categories, liveId]
                        : formData.categories.filter(c => c !== liveId);
                      setFormData({ ...formData, live: isChecked, categories: Array.from(new Set(newCats)) });
                    }} />
                    <Label htmlFor="live" className="text-[10px] font-bold cursor-pointer uppercase text-red-600 flex items-center gap-1">
                      <span className="h-2 w-2 rounded-full bg-red-600 animate-pulse"></span>
                      LIVE
                    </Label>
                  </div>
                  <div className="flex items-center space-x-2 bg-gray-50 p-2.5 rounded-lg border border-gray-100">
                    <Checkbox id="onreq" checked={formData.on_request} onCheckedChange={(v) => setFormData({ ...formData, on_request: !!v })} />
                    <Label htmlFor="onreq" className="text-[10px] font-bold cursor-pointer uppercase text-gray-500">Sob Consulta</Label>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Precificação */}
          <div className="bg-white p-8 rounded-2xl border border-gray-100 shadow-sm space-y-6">
            <h2 className="text-xl font-bold text-gray-700 flex items-center gap-2"><DollarSign className="h-5 w-5 text-[#D4C070]" /> Precificação</h2>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-x-6 gap-y-8">
              <div id="field-price" className="space-y-1">
                <Label>Preço à Vista <span className="text-red-500">*</span></Label>
                <div className="relative">
                  <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                  <Input className={cn(inputClass, "pl-9", errors.price && "border-red-500 focus:border-red-500 focus:ring-red-500")} type="number" value={formData.price} onChange={(e) => { setFormData({ ...formData, price: e.target.value, avista_price: e.target.value }); if (errors.price) setErrors(prev => ({ ...prev, price: false })); }} />
                </div>
              </div>
              <div className="space-y-1">
                <Label>Preço de Promoção (Sale)</Label>
                <div className="relative">
                  <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                  <Input className={cn(inputClass, "pl-9")} type="number" value={formData.sale_price} onChange={(e) => setFormData({ ...formData, sale_price: e.target.value })} placeholder="Opcional" />
                </div>
                <p className="text-[10px] text-gray-400">Ao preencher este valor, o produto é incluído automaticamente na categoria <b>SALE</b> do site.</p>
              </div>
              <div className="space-y-1">
                <Label>Comissão FDC (%)</Label>
                <Input
                  className={inputClass}
                  type="number"
                  min="0"
                  max="100"
                  value={formData.product_comission}
                  onChange={(e) => {
                    let val = e.target.value;
                    if (Number(val) > 100) val = "100";
                    setFormData({ ...formData, product_comission: val });
                  }}
                />
              </div>
              <div className="space-y-1">
                <Label>Comissão Indicação (%)</Label>
                <Input
                  className={inputClass}
                  type="number"
                  min="0"
                  max="100"
                  placeholder="Padrão"
                  value={formData.referral_commission_rate}
                  onChange={(e) => {
                    let val = e.target.value;
                    if (Number(val) > 100) val = "100";
                    setFormData({ ...formData, referral_commission_rate: val });
                  }}
                />
              </div>
              <div className="space-y-1">
                <Label>Certificado</Label>
                <Input className={inputClass} type="number" value={formData.certificate_price} onChange={(e) => setFormData({ ...formData, certificate_price: e.target.value })} />
              </div>

              <div className="md:col-span-2 space-y-3 p-4 bg-gray-50 rounded-xl border border-gray-100">
                <Label className="text-[#D4C070] font-bold uppercase text-[10px] tracking-widest">Configuração de Juros</Label>
                <div className="flex flex-col sm:flex-row gap-4">
                  <div className="flex-1 space-y-1">
                    <span className="text-[10px] text-gray-400 uppercase font-bold">Tipo</span>
                    <div className="flex bg-white rounded-lg border border-gray-200 p-1 h-10">
                      <button type="button" onClick={() => setFormData({ ...formData, juros_type: 'percent' })} className={cn("flex-1 rounded-md text-[10px] font-bold transition-all", formData.juros_type === 'percent' ? "bg-[#D4C070] text-white shadow-sm" : "text-gray-400 hover:text-gray-600")}>PORCENTAGEM (%)</button>
                      <button type="button" onClick={() => setFormData({ ...formData, juros_type: 'value' })} className={cn("flex-1 rounded-md text-[10px] font-bold transition-all", formData.juros_type === 'value' ? "bg-[#D4C070] text-white shadow-sm" : "text-gray-400 hover:text-gray-600")}>VALOR (R$)</button>
                    </div>
                  </div>
                  <div className="flex-1 space-y-1">
                    <span className="text-[10px] text-gray-400 uppercase font-bold">Valor</span>
                    <Input
                      className={inputClass}
                      type="number"
                      step="0.01"
                      min="0"
                      max={formData.juros_type === 'percent' ? "100" : undefined}
                      value={formData.juros}
                      onChange={(e) => {
                        let val = e.target.value;
                        if (formData.juros_type === 'percent' && Number(val) > 100) val = "100";
                        setFormData({ ...formData, juros: val });
                      }}
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Ficha Técnica */}
          <div className="bg-white p-8 rounded-2xl border border-gray-100 shadow-sm space-y-6">
            <h2 className="text-xl font-bold text-gray-700 flex items-center gap-2"><Percent className="h-5 w-5 text-[#D4C070]" /> Ficha Técnica</h2>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <div className="space-y-1">
                <Label>Estado de Conservação</Label>
                <Select value={formData.estado} onValueChange={(v) => setFormData({ ...formData, estado: v })}>
                  <SelectTrigger className={inputClass}><SelectValue placeholder="Selecione o estado..." /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Bom">Bom</SelectItem>
                    <SelectItem value="Muito Bom">Muito Bom</SelectItem>
                    <SelectItem value="Ótimo">Ótimo</SelectItem>
                    <SelectItem value="Excelente">Excelente</SelectItem>
                    <SelectItem value="Nunca Usado">Nunca Usado</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              {['cor', 'cor_ferragem', 'material', 'tamanho', 'numero', 'origem', 'salto', 'sola', 'adicionais'].map(f => (
                <div key={f} className="space-y-1">
                  <Label className="capitalize">{f === 'numero' ? 'Número (BRA)' : f.replace('_', ' ')}</Label>
                  <Input className={inputClass} value={(formData as any)[f]} onChange={(e) => setFormData({ ...formData, [f]: e.target.value })} />
                </div>
              ))}
              {formData.categories.some(c => ["49", "50", "51", "52", "112", "4617"].includes(String(c))) && (
                <div className="md:col-span-4 mt-2 space-y-4 bg-gray-50 p-4 rounded-xl border border-gray-100">
                  <div>
                    <h3 className="text-sm font-bold text-gray-700">Medidas da Bolsa</h3>
                    <p className="text-[10px] text-gray-500 font-medium mt-1">Campos utilizados para mostrar imagem de medidas no detalhe do produto.</p>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    {['altura', 'largura', 'profundidade'].map(f => (
                      <div key={f} className="space-y-1">
                        <Label className="capitalize">{f.replace('_', ' ')}</Label>
                        <Input className={inputClass} value={(formData as any)[f]} onChange={(e) => setFormData({ ...formData, [f]: e.target.value })} />
                      </div>
                    ))}
                  </div>
                </div>
              )}
              <div className="md:col-span-4 space-y-1">
                <Label>Descrição Completa</Label>
                <textarea className={cn(inputClass, "w-full h-32 p-3 rounded-lg border outline-none resize-none")} value={formData.description} onChange={(e) => setFormData({ ...formData, description: e.target.value })} />
              </div>
            </div>
          </div>

          {/* Galeria */}
          <div id="field-images" className="bg-white p-8 rounded-2xl border border-gray-100 shadow-sm space-y-8">
            <div className="flex justify-between items-center">
              <h2 className="text-xl font-bold text-gray-700 flex items-center gap-2"><Upload className="h-5 w-5 text-[#D4C070]" /> Galeria de Fotos <span className="text-red-500">*</span></h2>
              <span className="text-[10px] font-bold text-gray-400 uppercase bg-gray-50 px-3 py-1 rounded-full">{formData.images.length} Imagens</span>
            </div>
            <div className={cn("relative border-2 border-dashed rounded-3xl p-12 text-center group cursor-pointer transition-colors overflow-hidden", loading ? "border-[#D4C070]" : errors.images ? "border-red-500 hover:border-red-500 bg-red-50/5" : "border-gray-200 hover:border-[#D4C070] bg-gray-50/30")} onDragOver={(e) => e.preventDefault()} onDrop={handleImageUpload} onClick={() => !loading && document.getElementById('img-up')?.click()}>
              <input id="img-up" type="file" multiple className="hidden" onChange={handleImageUpload} accept="image/*" />
              <Plus className="mx-auto h-10 w-10 text-[#D4C070] mb-2" />
              <p className="text-sm font-semibold text-gray-600">Arraste ou clique para enviar fotos em lote</p>
              <p className="text-xs text-gray-400 mt-1">Selecione vários arquivos (PNG, JPG, WEBP de até 25MB)</p>
              {uploadProgress && (
                <div className="absolute inset-0 flex flex-col items-center justify-center bg-white/95 z-10 p-4 gap-2">
                  <div className="animate-spin rounded-full h-9 w-9 border-4 border-[#D4C070] border-t-transparent" />
                  <p className="text-sm font-bold text-gray-800">
                    Enviando fotos: <span className="text-[#D4C070]">{uploadProgress.current}</span> / {uploadProgress.total}
                  </p>
                  {uploadProgress.filename && (
                    <p className="text-xs text-gray-500 font-mono truncate max-w-xs">
                      {uploadProgress.filename}
                    </p>
                  )}
                  <div className="w-56 bg-gray-100 h-2.5 rounded-full overflow-hidden mt-1 border border-gray-200">
                    <div
                      className="bg-[#D4C070] h-full transition-all duration-300 rounded-full"
                      style={{ width: `${Math.round((uploadProgress.current / uploadProgress.total) * 100)}%` }}
                    />
                  </div>
                </div>
              )}
            </div>
            <div className="grid grid-cols-2 md:grid-cols-5 gap-4 md:gap-6">
              {formData.images.map((img, i) => (
                <div key={i} className={cn("relative group aspect-[3/4] rounded-xl overflow-hidden border-2 transition-all bg-gray-100 flex flex-col justify-between select-none", i === 0 ? "border-[#D4C070] shadow-sm" : "border-gray-200 shadow-xs")}>
                  <img src={getImageUrl(img.image)} className="w-full h-full object-cover absolute inset-0" />

                  {/* Badges de Capa e Posição */}
                  <div className="relative z-10 p-2 flex justify-between items-center w-full pointer-events-none">
                    {i === 0 ? (
                      <span className="bg-[#D4C070] text-white px-2 py-0.5 rounded text-[9px] font-bold uppercase shadow-sm pointer-events-auto">Capa</span>
                    ) : <span />}
                    <span className="bg-black/60 backdrop-blur-xs text-white px-2 py-0.5 rounded-full text-[9px] font-bold shadow-sm pointer-events-auto">
                      #{i + 1}
                    </span>
                  </div>

                  {/* Controles de Ordenação e Ações (Sempre visíveis no Mobile, Hover no Desktop) */}
                  <div className="relative z-10 p-2 bg-gradient-to-t from-black/80 via-black/40 to-transparent flex items-center justify-center gap-1.5 opacity-100 md:opacity-0 md:group-hover:opacity-100 transition-opacity">
                    <button
                      type="button"
                      disabled={i === 0}
                      onClick={(e) => { e.stopPropagation(); moveImage(i, 'left'); }}
                      className="bg-white/20 hover:bg-white/40 active:bg-white/50 disabled:opacity-30 text-white p-1.5 rounded-full transition-colors touch-manipulation cursor-pointer"
                      title="Mover para esquerda"
                    >
                      <ChevronLeft className="h-4 w-4" />
                    </button>

                    <button
                      type="button"
                      onClick={(e) => { e.stopPropagation(); handleDownloadSingleImage(img.image, i); }}
                      className="bg-white/20 hover:bg-white/40 active:bg-white/50 text-white p-1.5 rounded-full transition-colors touch-manipulation cursor-pointer"
                      title="Baixar foto"
                    >
                      <Download className="h-4 w-4" />
                    </button>

                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        const newImgs = formData.images.filter((_, idx) => idx !== i);
                        setFormData({ ...formData, images: newImgs });
                        if (newImgs.length === 0) setErrors(prev => ({ ...prev, images: true }));
                      }}
                      className="bg-red-500/90 hover:bg-red-600 active:bg-red-700 text-white p-1.5 rounded-full shadow-xs transition-colors touch-manipulation cursor-pointer"
                      title="Excluir foto"
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>

                    <button
                      type="button"
                      disabled={i === formData.images.length - 1}
                      onClick={(e) => { e.stopPropagation(); moveImage(i, 'right'); }}
                      className="bg-white/20 hover:bg-white/40 active:bg-white/50 disabled:opacity-30 text-white p-1.5 rounded-full transition-colors touch-manipulation cursor-pointer"
                      title="Mover para direita"
                    >
                      <ChevronRight className="h-4 w-4" />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* SEO */}
          <div className="bg-white p-8 rounded-2xl border border-gray-100 shadow-sm space-y-6">
            <h2 className="text-xl font-bold text-gray-700 flex items-center gap-2"><Eye className="h-5 w-5 text-[#D4C070]" /> Configurações de SEO</h2>
            <div className="space-y-4">
              <div className="space-y-1">
                <Label>SEO Title (Título da Página)</Label>
                <Input
                  className={inputClass}
                  placeholder={formData.name || "Título para buscadores..."}
                  value={formData.seo_title}
                  onChange={(e) => setFormData({ ...formData, seo_title: e.target.value })}
                />
                <p className="text-[10px] text-gray-400">Recomendado: até 60 caracteres.</p>
              </div>
              <div className="space-y-1">
                <Label>SEO Description (Meta Descrição)</Label>
                <textarea
                  className={cn(inputClass, "w-full h-24 p-3 rounded-lg border outline-none resize-none")}
                  placeholder={formData.description || "Descrição curta para aparecer no Google..."}
                  value={formData.seo_description}
                  onChange={(e) => setFormData({ ...formData, seo_description: e.target.value })}
                />
                <p className="text-[10px] text-gray-400">Recomendado: até 160 caracteres.</p>
              </div>
            </div>
          </div>

          {/* Status e Venda */}
          <div className="bg-white p-8 rounded-2xl border border-gray-100 shadow-sm space-y-6">
            <h2 className="text-xl font-bold text-gray-700 flex items-center gap-2"><Settings className="h-5 w-5 text-[#D4C070]" /> Status e Venda</h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="space-y-1">
                <Label>Status do Produto</Label>
                <Select value={formData.status} onValueChange={(v) => setFormData({ ...formData, status: v })}>
                  <SelectTrigger className={inputClass}><SelectValue placeholder="Selecione..." /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="published">Publicado</SelectItem>
                    <SelectItem value="draft">Rascunho</SelectItem>
                    <SelectItem value="deleted">Excluído</SelectItem>
                    <SelectItem value="returned">Em Trânsito</SelectItem>
                    <SelectItem value="desistence">Devolvido</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1">
                <Label>Data de Disponibilidade (Publicação)</Label>
                <Input
                  type="datetime-local"
                  className={inputClass}
                  value={formData.available_at ? formData.available_at.substring(0, 16).replace(" ", "T") : ""}
                  onChange={(e) => setFormData({ ...formData, available_at: e.target.value })}
                />
                <p className="text-[10px] text-gray-400">Data em que o produto vai aparecer na loja.</p>
              </div>
              <div id="field-quantity" className="space-y-1">
                <Label>Quantidade em Estoque <span className="text-red-500">*</span></Label>
                <Input
                  type="number"
                  min="0"
                  className={cn(inputClass, errors.quantity && "border-red-500 focus:border-red-500 focus:ring-red-500")}
                  value={formData.quantity}
                  onChange={(e) => { setFormData({ ...formData, quantity: e.target.value }); if (errors.quantity) setErrors(prev => ({ ...prev, quantity: false })); }}
                />
              </div>
              <div className="space-y-1">
                <Label>Data da Venda</Label>
                <Input
                  type="datetime-local"
                  className={inputClass}
                  value={formData.saled_at ? formData.saled_at.substring(0, 16).replace(" ", "T") : ""}
                  onChange={(e) => setFormData({ ...formData, saled_at: e.target.value })}
                />
                <p className="text-[10px] text-gray-400">Preencha data e hora se o produto já foi vendido.</p>
              </div>
              {formData.status === 'desistence' && (
                <div className="space-y-1">
                  <Label>Data de Devolução</Label>
                  <Input
                    type="datetime-local"
                    className={inputClass}
                    value={formData.returned_at ? formData.returned_at.substring(0, 16).replace(" ", "T") : ""}
                    onChange={(e) => setFormData({ ...formData, returned_at: e.target.value })}
                  />
                  <p className="text-[10px] text-gray-400">Preencha a data e hora em que o produto foi devolvido ao cliente/fornecedor.</p>
                </div>
              )}
            </div>
          </div>

          {/* Floating Action Bar */}
          <div className="fixed bottom-0 left-0 right-0 md:left-64 bg-white/90 backdrop-blur-md border-t p-4 flex items-center justify-between z-50 shadow-2xl">
            <span className="hidden md:block text-xs text-gray-500">Editando: <b>{formData.name || "Novo Produto"}</b></span>
            <div className="flex items-center gap-3 w-full md:w-auto justify-end">
              <div className="flex items-center gap-2 mr-2">
                <Checkbox
                  id="bar_bump_available_at"
                  checked={formData.bump_available_at}
                  onCheckedChange={(v) => setFormData({ ...formData, bump_available_at: !!v })}
                />
                <Label htmlFor="bar_bump_available_at" className="text-xs font-medium cursor-pointer text-gray-700">
                  Subir para 1º ao salvar
                </Label>
              </div>
              <Button type="button" variant="outline" onClick={onBack} className="flex-1 md:flex-none">Cancelar</Button>
              <Button type="submit" className="bg-[#D4C070] text-white px-10 flex-1 md:flex-none shadow-lg shadow-[#D4C070]/20 font-bold">{loading ? "Salvando..." : (productData ? "Atualizar Produto" : "Publicar Produto")}</Button>
            </div>
          </div>
        </form>
      </div>

      <AddBrandModal
        isOpen={modals.brand}
        onClose={() => setModals({ ...modals, brand: false })}
        onSuccess={(d) => {
          const brand = d.data || d;
          const id = String(brand.id);
          setFormData(prev => ({ ...prev, brand_id: id }));
          setSelectedNames(prev => ({ ...prev, [id]: brand.name }));
          setBrands(prev => [...prev, brand]);
        }}
      />
      <AddCategoryModal
        isOpen={modals.category}
        onClose={() => setModals({ ...modals, category: false })}
        categories={categories}
        onSuccess={(d) => {
          const cat = d.data || d;
          const id = String(cat.id);
          const name = decodeHtmlEntities(cat.name);
          // Se for subcategoria, tenta montar o nome com o pai
          let fullName = name;
          if (cat.parent_id > 0) {
            const parent = categories.find(c => String(c.id) === String(cat.parent_id));
            if (parent) fullName = `${parent.name} > ${name}`;
          }
          setCategories(prev => [...prev, { ...cat, name: fullName }]);
          setFormData(prev => ({ ...prev, categories: [...prev.categories, id] }));
        }}
      />
      <AddInfluencerModal
        isOpen={modals.influencer}
        onClose={() => setModals({ ...modals, influencer: false })}
        onSuccess={(d) => {
          const inf = d.data || d;
          const id = String(inf.id);
          setFormData(prev => ({ ...prev, influencer_id: id }));
          setSelectedNames(prev => ({ ...prev, [id]: inf.name }));
        }}
      />
      <AddSupplierModal
        isOpen={modals.supplier}
        onClose={() => setModals({ ...modals, supplier: false })}
        onSuccess={(d) => {
          let u = d;
          if (u.data) u = u.data;
          if (u.data) u = u.data;

          if (!u || !u.id) {
            toast.error("Erro ao selecionar o fornecedor recém-cadastrado.");
            return;
          }

          const id = String(u.id);
          const fullName = [u.first_name, u.last_name].filter(Boolean).join(" ") || u.name || "";
          const display = `[${u.code || id}] ${fullName}${u.email ? ` (${u.email})` : ""}`;
          const nextSku = u.code ? String(u.code) : id;

          setFormData(prev => ({
            ...prev,
            user_id: id,
            sku: nextSku,
            slug: slugify(prev.name + " " + nextSku)
          }));
          setSelectedNames(prev => ({ ...prev, [id]: display }));
          setErrors(prev => ({ ...prev, user_id: false }));
        }}
      />
      <AddShippingModal
        isOpen={modals.shipping}
        onClose={() => setModals({ ...modals, shipping: false })}
        onSuccess={(d) => {
          const ship = d.data || d;
          const id = String(ship.id);
          setShippings(prev => [...prev, ship]);
          setFormData(prev => ({ ...prev, shipping_id: id }));
          setSelectedNames(prev => ({ ...prev, [id]: ship.name }));
        }}
      />
    </main>
  );
}
